import { get, post, put, del } from '../apiClient';
import { API_ENDPOINTS, STORAGE_KEYS } from '../config';
import { applyCsrfFromResponse, clearCsrfToken } from '../csrf';
import { refreshSessionWithQueue } from '../sessionRefresh';
import { setSessionActive, clearSessionActive } from '../../utils/authRouteUtils';

const AUTH_MODE = 'cookie';

const authService = {
  login: async (email, password, rememberMe = false) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
        rememberMe,
        authMode: AUTH_MODE
      });

      if (response && response.responseCode === 200) {
        applyCsrfFromResponse(response.data);
        setSessionActive();

        if (rememberMe) {
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
        } else {
          localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        }

        return {
          success: true,
          user: response.data?.user || null
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Login failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  },

  loginWithEmailOTP: async (email) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.LOGIN_EMAIL_OTP, { email });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to send OTP'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  verifyEmailOTP: async (email, otp, rememberMe = false) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.VERIFY_EMAIL_OTP, {
        email,
        otp,
        rememberMe,
        authMode: AUTH_MODE
      });

      if (response.responseCode === 200) {
        if (response.data?.user) {
          applyCsrfFromResponse(response.data);
          setSessionActive();

          if (rememberMe) {
            localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
          } else {
            localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
          }

          return {
            success: true,
            user: response.data.user
          };
        }

        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'OTP verification failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'OTP verification failed'
      };
    }
  },

  loginMobileOTP: async (mobile) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.LOGIN_MOBILE_OTP, { mobile });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to send OTP'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  verifyMobileOTP: async (mobile, otp, rememberMe = false) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.VERIFY_MOBILE_OTP, {
        mobile,
        otp,
        rememberMe,
        authMode: AUTH_MODE
      });

      if (response.responseCode === 200) {
        if (response.data?.user) {
          applyCsrfFromResponse(response.data);
          setSessionActive();

          if (rememberMe) {
            localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
          } else {
            localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
          }

          return {
            success: true,
            user: response.data.user
          };
        }

        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'OTP verification failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'OTP verification failed'
      };
    }
  },

  forgotPassword: async (email) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send reset email'
      };
    }
  },

  sendOTP: async (data) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        email: data.emailOrPhone
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to send OTP'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  verifyOTP: async (data) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
        email: data.emailOrPhone,
        otp: data.otp
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'OTP verification failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'OTP verification failed'
      };
    }
  },

  resetPassword: async (data) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        email: data.emailOrPhone,
        otp: data.otp,
        password: data.newPassword
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Password reset failed',
        errors: response.errors
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Password reset failed'
      };
    }
  },

  logout: async () => {
    try {
      await post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      // ignore
    } finally {
      clearCsrfToken();
      clearSessionActive();
    }
    return { success: true, message: 'Logout successful' };
  },

  getCurrentUser: async () => {
    try {
      const response = await get(API_ENDPOINTS.AUTH.ME);
      if (response.responseCode === 200 && response.data?.user) {
        applyCsrfFromResponse(response.data);
        return response.data.user;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  refreshSession: async () => {
    try {
      await refreshSessionWithQueue();
      const response = await get(API_ENDPOINTS.AUTH.ME);
      if (response.responseCode === 200 && response.data?.user) {
        applyCsrfFromResponse(response.data);
        return { success: true, user: response.data.user };
      }
      return { success: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  isAuthenticated: async () => {
    const user = await authService.getCurrentUser();
    return !!user;
  }
};

export default authService;
